import { z } from "zod";
import { CycleAudienceActionSchemas } from "./cycleAudienceSchemas.ts";
import { CycleQuestionnaireActionSchemas, CycleQuestionnaireSectionSchema, type CycleQuestionnaireSection } from "./cycleQuestionnaireSchemas.ts";
import { CycleDueDatesSchema, CycleScheduleActionSchemas, CycleScheduleSchema } from "./cycleScheduleSchemas.ts";
import { CYCLE_DESCRIPTION_MAX_LENGTH, CYCLE_NAME_MAX_LENGTH, RECOMMENDED_PEER_REVIEWS_MAX, RECOMMENDED_PEER_REVIEWS_MIN, SCORE_SCALE_MAX, SCORE_SCALE_MIN } from "./reviewConstraints.ts";
import { CycleReviewTypes, type CycleReviewType } from "./reviewTypes.ts";

export const CycleStates = ["draft", "scheduled", "active", "locked"] as const;

export type CycleState = (typeof CycleStates)[number];

export const FilterableCycleStates = ["draft", "active", "locked"] as const satisfies readonly CycleState[];

const CycleActions = ["edit", "duplicate", "lock", "unlock", "delete"] as const;

const CycleActionUnavailableReasons = ["locked", "notLaunched"] as const;

const CycleUnavailableActionsSchema = z.partialRecord(z.enum(CycleActions), z.enum(CycleActionUnavailableReasons));

export type CycleUnavailableActions = z.infer<typeof CycleUnavailableActionsSchema>;

export type { CycleReviewType };

const AcknowledgeableReviewTypes = ["manager", "peer", "upward"] as const satisfies readonly CycleReviewType[];

export function hasAcknowledgeableReviewType(reviewTypes: readonly CycleReviewType[]): boolean {
    return reviewTypes.some(reviewType => AcknowledgeableReviewTypes.some(acknowledgeable => acknowledgeable === reviewType));
}

export function canRequireAcknowledgment(reviewTypes: readonly CycleReviewType[], sections: readonly CycleQuestionnaireSection[]): boolean {
    return AcknowledgeableReviewTypes.some(
        reviewType =>
            reviewTypes.includes(reviewType) &&
            sections.some(section =>
                section.questions.some(question => {
                    const audience = question.audiences[reviewType];

                    return audience.isAsked && audience.sharePolicy === "visible";
                })
            )
    );
}

export const CycleReminderTypes = ["self-review", "manager-review", "peer-review", "upward-review", "not-shared", "not-acknowledged"] as const;

export type CycleReminderType = (typeof CycleReminderTypes)[number];

export const REMINDER_MESSAGE_MAX_LENGTH = 10000;

export const CycleReminderInfoSchema = z.object({
    reminderType: z.enum(CycleReminderTypes),
    recipientIds: z.array(z.string()),
    wasSentRecently: z.boolean()
});

export type CycleReminderInfo = z.infer<typeof CycleReminderInfoSchema>;

export const CycleReminderInformationSchema = z.object({
    reminders: z.array(CycleReminderInfoSchema),
    recentWindowHours: z.number()
});

export type CycleReminderInformation = z.infer<typeof CycleReminderInformationSchema>;

export const CyclePresets = ["all-active", "involving-me", "created-by-me"] as const;

export type CyclePreset = (typeof CyclePresets)[number];

export const CyclePersonSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    imageUrl: z.string().optional()
});

export type CyclePerson = z.infer<typeof CyclePersonSchema>;

export const CycleProgressSchema = z.object({
    submitted: z.number(),
    total: z.number()
});

export type CycleProgress = z.infer<typeof CycleProgressSchema>;

const KeepsAdmittingSchema = z.boolean();

export const ReviewCycleSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    state: z.enum(CycleStates),
    revieweeCount: z.number(),
    keepsAdmitting: KeepsAdmittingSchema,
    reviewTypes: z.array(z.enum(CycleReviewTypes)),
    evaluationStartDate: z.string().optional(),
    evaluationEndDate: z.string().optional(),
    creator: CyclePersonSchema.optional(),
    progress: CycleProgressSchema.optional(),
    unavailableActions: CycleUnavailableActionsSchema
});

export type ReviewCycleSummary = z.infer<typeof ReviewCycleSummarySchema>;

export const CycleTypeProgressSchema = z.object({
    reviewType: z.enum(CycleReviewTypes),
    notStarted: z.number(),
    inProgress: z.number(),
    submitted: z.number(),
    overdue: z.number(),
    total: z.number(),
    dueDate: z.string().optional()
});

export type CycleTypeProgress = z.infer<typeof CycleTypeProgressSchema>;

const CycleScoreFormulaModes = ["average-all-questions", "average-manager-questions", "custom", "overall-score"] as const;

export type CycleScoreFormulaMode = (typeof CycleScoreFormulaModes)[number];

const CycleReviewScoreFormulaModes = ["average-manager-questions", "average-all-questions", "custom"] as const satisfies readonly CycleScoreFormulaMode[];

const CyclePerformanceFormulaModes = ["overall-score", "custom"] as const satisfies readonly CycleScoreFormulaMode[];

const CyclePotentialFormulaModes = ["custom"] as const satisfies readonly CycleScoreFormulaMode[];

const CycleScoreFormulaComponentSchema = z.object({
    questionId: z.string(),
    title: z.string(),
    reviewType: z.enum(CycleReviewTypes),
    weight: z.number().nullable(),
    scaleMax: z.number().nullable()
});

const CycleScoreFormulaSchema = z.object({
    mode: z.enum(CycleScoreFormulaModes),
    configuredMode: z.enum(CycleScoreFormulaModes),
    scale: z.number(),
    components: z.array(CycleScoreFormulaComponentSchema)
});

export type CycleScoreFormula = z.infer<typeof CycleScoreFormulaSchema>;

const CycleScoreFormulaComponentUpsertSchema = z.object({
    questionId: z.string().min(1),
    reviewType: z.enum(CycleReviewTypes),
    weight: z.number().gt(0).max(1).nullable()
});

export type CycleScoreFormulaComponentUpsert = z.infer<typeof CycleScoreFormulaComponentUpsertSchema>;

function scoreFormulaUpsertSchema<const TModes extends readonly [CycleScoreFormulaMode, ...CycleScoreFormulaMode[]]>(modes: TModes) {
    return z.object({
        mode: z.enum(modes),
        scale: z.number().int().min(SCORE_SCALE_MIN).max(SCORE_SCALE_MAX),
        components: z.array(CycleScoreFormulaComponentUpsertSchema)
    });
}

const CycleScoreFormulaUpsertSchema = scoreFormulaUpsertSchema(CycleScoreFormulaModes);

export type CycleScoreFormulaUpsert = z.infer<typeof CycleScoreFormulaUpsertSchema>;

const CycleReviewScoreFormulaUpsertSchema = scoreFormulaUpsertSchema(CycleReviewScoreFormulaModes);

export type CycleReviewScoreFormulaUpsert = z.infer<typeof CycleReviewScoreFormulaUpsertSchema>;

const CyclePerformanceFormulaUpsertSchema = scoreFormulaUpsertSchema(CyclePerformanceFormulaModes);

export type CyclePerformanceFormulaUpsert = z.infer<typeof CyclePerformanceFormulaUpsertSchema>;

const CyclePotentialFormulaUpsertSchema = scoreFormulaUpsertSchema(CyclePotentialFormulaModes);

export type CyclePotentialFormulaUpsert = z.infer<typeof CyclePotentialFormulaUpsertSchema>;

const CycleScoreFormulaActionSchemas = [
    z.object({ type: z.literal("UpdateReviewCycleReviewScoreFormulaAction"), formula: CycleReviewScoreFormulaUpsertSchema.optional() }),
    z.object({ type: z.literal("UpdateReviewCyclePerformanceScoreFormulaAction"), formula: CyclePerformanceFormulaUpsertSchema.optional() }),
    z.object({ type: z.literal("UpdateReviewCyclePotentialScoreFormulaAction"), formula: CyclePotentialFormulaUpsertSchema.optional() })
] as const;

export const CycleReviewVisibilities = ["anonymous", "identified"] as const;

export type CycleReviewVisibility = (typeof CycleReviewVisibilities)[number];

export const CycleUpwardNominationModes = ["direct-reports", "reporting-line", "manual"] as const;

export type CycleUpwardNominationMode = (typeof CycleUpwardNominationModes)[number];

export const CycleFrozenFields = ["reviewTypes", "upwardNominationMode", "acknowledgment", "questionKind", "scheduleType", "cohortInterval", "milestoneStartOffset", "automatedStartMode", "automatedStartDate", "audience"] as const;

export type CycleFrozenField = (typeof CycleFrozenFields)[number];

export const ReviewCycleDetailSchema = z.object({
    id: z.string(),
    name: z.string(),
    state: z.enum(CycleStates),
    keepsAdmitting: KeepsAdmittingSchema,
    reviewTypes: z.array(z.enum(CycleReviewTypes)),
    evaluationStartDate: z.string().optional(),
    evaluationEndDate: z.string().optional(),
    launchDate: z.string().optional(),
    lockedDate: z.string().optional(),
    automatedStartDate: z.string().optional(),
    creator: CyclePersonSchema.optional(),
    scoreFormula: CycleScoreFormulaSchema.optional(),
    performanceFormula: CycleScoreFormulaSchema.optional(),
    potentialFormula: CycleScoreFormulaSchema.optional(),
    isAcknowledgmentRequired: z.boolean(),
    peerReviewVisibility: z.enum(CycleReviewVisibilities).optional(),
    upwardReviewVisibility: z.enum(CycleReviewVisibilities).optional(),
    hasSharedPeerQuestions: z.boolean(),
    hasSharedUpwardQuestions: z.boolean(),
    recommendedPeerReviewsCount: z.number().optional(),
    description: z.string().optional(),
    upwardReviewNominationMode: z.enum(CycleUpwardNominationModes).optional(),
    sections: z.array(CycleQuestionnaireSectionSchema),
    schedule: CycleScheduleSchema,
    dueDates: CycleDueDatesSchema,
    unavailableActions: CycleUnavailableActionsSchema,
    frozenFields: z.array(z.enum(CycleFrozenFields)),
    organizationTimeZone: z.string().optional()
});

export type ReviewCycleDetail = z.infer<typeof ReviewCycleDetailSchema>;

export const CycleScopedProgressSchema = z.object({
    progress: z.array(CycleTypeProgressSchema).optional(),
    hasUpcomingReviewees: z.boolean(),
    nextEntryDate: z.string().optional()
});

export type CycleScopedProgress = z.infer<typeof CycleScopedProgressSchema>;

export const CycleRevieweeStatuses = ["not-started", "in-progress", "submitted", "overdue", "not-submitted"] as const;

export type CycleRevieweeStatus = (typeof CycleRevieweeStatuses)[number];

export const CycleRevieweeReviewSchema = z.object({
    id: z.string(),
    reviewType: z.enum(CycleReviewTypes),
    status: z.enum(CycleRevieweeStatuses),
    reviewer: CyclePersonSchema.optional(),
    canWrite: z.boolean(),
    dueDate: z.string().optional()
});

export type CycleRevieweeReview = z.infer<typeof CycleRevieweeReviewSchema>;

export const CycleDeliveryStates = ["not-delivered", "shared", "acknowledged"] as const;

export type CycleDeliveryState = (typeof CycleDeliveryStates)[number];

export const CycleRevieweeStates = ["active", "upcoming"] as const;

export type CycleRevieweeState = (typeof CycleRevieweeStates)[number];

export const CycleRosterSortColumns = ["name", "entry-date"] as const;

export type CycleRosterSortColumn = (typeof CycleRosterSortColumns)[number];

export const CycleRevieweeSchema = z.object({
    member: CyclePersonSchema,
    jobTitle: z.string().optional(),
    reportingManager: CyclePersonSchema.extend({
        jobTitle: z.string().optional()
    }).optional(),
    reviews: z.array(CycleRevieweeReviewSchema),
    deliveryState: z.enum(CycleDeliveryStates),
    state: z.enum(CycleRevieweeStates),
    hireDate: z.string().nullable(),
    entryDate: z.string().optional()
});

export type CycleReviewee = z.infer<typeof CycleRevieweeSchema>;

export const CycleActionAvailabilitySchema = z.object({
    shareableCount: z.number().nullable(),
    unshareableCount: z.number()
});

export const ReviewCycleActionSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("UpdateReviewCycleTitleAction"), title: z.string().max(CYCLE_NAME_MAX_LENGTH) }),
    z.object({ type: z.literal("UpdateReviewCycleDescriptionAction"), description: z.string().max(CYCLE_DESCRIPTION_MAX_LENGTH) }),
    z.object({ type: z.literal("UpdateReviewCycleReviewTypesAction"), reviewTypes: z.array(z.enum(CycleReviewTypes)).nonempty() }),
    z.object({
        type: z.literal("UpdateReviewCyclePeerSettingsAction"),
        recommendedPeerReviewsCount: z.number().int().min(RECOMMENDED_PEER_REVIEWS_MIN).max(RECOMMENDED_PEER_REVIEWS_MAX).optional(),
        visibility: z.enum(CycleReviewVisibilities).optional()
    }),
    z.object({
        type: z.literal("UpdateReviewCycleUpwardSettingsAction"),
        visibility: z.enum(CycleReviewVisibilities).optional(),
        nominationMode: z.enum(CycleUpwardNominationModes).optional()
    }),
    z.object({ type: z.literal("UpdateReviewCycleAcknowledgmentRequiredAction"), isAcknowledgmentRequired: z.boolean() }),
    ...CycleQuestionnaireActionSchemas,
    ...CycleScoreFormulaActionSchemas,
    ...CycleScheduleActionSchemas,
    ...CycleAudienceActionSchemas
]);

export type ReviewCycleAction = z.infer<typeof ReviewCycleActionSchema>;
